import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/auth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { ApiError } from '../../lib/api';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { Icon } from '../../components/ui/Icon';
import '../auth/auth.css';

export function SignupPage() {
  const { signup, login } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { verificationRequired } = await signup(email, password, fullName);
      if (!verificationRequired) {
        // Email confirmation is switched off on this server: the account is ready, so sign straight in.
        try {
          await login(email, password);
          navigate('/dashboard/sites', { replace: true });
        } catch {
          setError('An account with this email may already exist. Try signing in, or use a different email.');
        }
        return;
      }
      setMessage('Verification code sent. Please check your email.');
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not create your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-topbar">
        <Link className="auth-brand" to="/">
          <span className="auth-logo-mark" aria-hidden="true"><Icon name="zap" size={16} /></span>
          <span>Sarnia Builder</span>
        </Link>
        <ThemeToggle />
      </div>
      <div className="auth-main">
        <div className="auth-form-panel">
          <div className="auth-form-card">
            <h1>Create your account</h1>
            <form onSubmit={handleSubmit}>
              {error ? <Alert variant="error">{error}</Alert> : null}
              {message ? <Alert variant="success">{message}</Alert> : null}
              <Input
                label="Full name"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                label="Password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                hint="At least 8 characters."
                required
              />
              <Button type="submit" loading={submitting} style={{ width: '100%' }}>
                Sign up
              </Button>
              <p className="auth-form-footer">
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </form>
          </div>
        </div>
        <aside className="auth-side">
          <div className="auth-side__content">
            <h2 className="auth-side__tagline">Small island. Big signal.</h2>
            <ul className="auth-side__list">
              <li className="auth-side__item"><Icon name="check" size={20} /> Visual editing, no code guesswork</li>
              <li className="auth-side__item"><Icon name="check" size={20} /> Designer templates made in Guernsey</li>
              <li className="auth-side__item"><Icon name="check" size={20} /> Free previews and one-click export</li>
            </ul>
          </div>
          <div className="auth-side__preview">
            <img src="/template-previews/design-12.jpg" alt="Sarnia Builder template preview" loading="lazy" />
          </div>
        </aside>
      </div>
    </div>
  );
}
